import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, ExternalLink } from "lucide-react";
import VideoPlayer from "./VideoPlayer";
import { YoutubeWatchButton } from "./WatchActions";
import { getYoutubeId } from "../lib/api";
import { getVideoSourceType } from "../lib/video";
import { optimizeImageUrl, CARD_IMAGE_WIDTH } from "../lib/images";

export default function ProgramVideoSection({ videos }) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (videos.length) setActive(videos[0]);
    else setActive(null);
  }, [videos]);

  if (!videos.length) {
    return (
      <div className="tv-card p-10 text-center text-[var(--tv-muted)]">
        Bu programa ait video henüz eklenmedi.
      </div>
    );
  }

  const sourceType = getVideoSourceType(active);
  const hasYoutube = Boolean(getYoutubeId(active?.youtubeUrl));

  return (
    <div>
      <div className="tv-card !overflow-hidden mb-8">
        <div className="relative aspect-video bg-black">
          <VideoPlayer video={active} autoplay key={active?.id} />
        </div>
        {active && (
          <div className="p-5 md:p-6 border-t border-[var(--tv-border)]">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {sourceType === "upload" && <span className="tv-badge">Yüklenen video</span>}
              {sourceType === "youtube" && <span className="tv-badge">YouTube</span>}
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white font-display">{active.title}</h3>
            {active.description && (
              <p className="text-[var(--tv-muted)] mt-2 text-sm leading-relaxed">{active.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-[var(--tv-muted)]">
              {active.publishedAt && <span>{active.publishedAt}</span>}
              {active.views && (
                <span className="flex items-center gap-1">
                  <Eye size={12} /> {active.views}
                </span>
              )}
              {active.duration && <span>{active.duration}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-[var(--tv-border)]">
              <Link
                to={`/videolar/${active.id}`}
                className="tv-btn-primary !py-2.5 !px-5 text-sm inline-flex items-center gap-2"
              >
                <ExternalLink size={16} /> Detay sayfasında izle
              </Link>
              {hasYoutube && <YoutubeWatchButton url={active.youtubeUrl} className="!py-2.5 !px-4 text-sm" />}
            </div>
          </div>
        )}
      </div>

      <p className="text-sm text-[var(--tv-muted)] mb-4">
        Aşağıdan başka bir bölüm seçin veya detay sayfasına gidin.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {videos.map((v, i) => (
          <VideoListItem
            key={v.id}
            video={v}
            index={i}
            isActive={active?.id === v.id}
            onSelect={() => setActive(v)}
          />
        ))}
      </div>
    </div>
  );
}

function VideoListItem({ video, index, isActive, onSelect }) {
  return (
    <div
      className={`tv-fade-in tv-card overflow-hidden transition-all ${
        isActive ? "ring-2 ring-[var(--tv-purple-bright)] border-[rgba(168,85,247,0.5)]" : ""
      }`}
      style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
    >
      <button type="button" onClick={onSelect} className="w-full text-left group">
        <div className="relative aspect-video overflow-hidden">
          <img
            src={optimizeImageUrl(video.coverImage || "/images/hero.jpg", CARD_IMAGE_WIDTH)}
            alt={video.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors" />
          {isActive && (
            <span className="absolute top-2 left-2 tv-badge-live text-[10px]">İZLENİYOR</span>
          )}
        </div>
        <div className="p-3">
          <h4 className="text-white font-semibold text-sm line-clamp-2 group-hover:text-[var(--tv-purple-bright)] transition-colors">
            {video.title}
          </h4>
        </div>
      </button>
      <div className="px-3 pb-3">
        <Link
          to={`/videolar/${video.id}`}
          className="inline-flex w-full items-center justify-center rounded-lg border border-[var(--tv-border)] py-2 text-sm text-[var(--tv-text)] hover:border-[rgba(168,85,247,0.5)] hover:bg-[rgba(139,92,246,0.08)] transition-colors"
        >
          Detay
        </Link>
      </div>
    </div>
  );
}
